"use client";

import React, { useState } from "react";
import { Check, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const pricingTiers = [{
  name: "Woodlot",
  description: "Precision inventory for private family forestland owners.",
  priceMonthly: 450,
  priceYearly: 395,
  features: ["Up to 250 Total Acres", "Full Forest Type Breakdown", "Merchantable Volume Report", "Digital Stand Boundary Map", "7-Day Turnaround Time"],
  ctaText: "Order Analysis",
  ctaLink: "/PurchasePage",
  popular: false
}, {
  name: "Estate",
  description: "Comprehensive data for mid-sized timber holdings.",
  priceMonthly: 850,
  priceYearly: 725,
  features: ["Up to 1,000 Total Acres", "DBH Distribution Charts", "Product Class Valuation", "LiDAR Point Cloud Preview", "Priority AI Processing", "Forester-Ready Data Export"],
  ctaText: "Order Analysis",
  ctaLink: "/PurchasePage",
  popular: true
}, {
  name: "Enterprise",
  description: "Scalable analytics for TIMOs and large forest agencies.",
  priceMonthly: 1800,
  priceYearly: 1500,
  features: ["Over 1,000+ Acres", "Custom API Data Access", "Multi-Stand Comparison", "Dedicated Project Engineer", "White-label Reporting", "Historical Growth Modeling"],
  ctaText: "Contact Sales",
  ctaLink: "/ContactPage",
  popular: false
}];
export default function ChooseYourFlavr() {
  const [isYearly, setIsYearly] = useState(false);
  return <section data-section-id="224592" id="pricing-options" className="bg-background text-foreground py-24 px-4 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[500px] bg-primary/5 rounded-full blur-[120px] pointer-events-none opacity-30" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="outline" className="mb-4 border-primary/30 text-accent-foreground bg-accent backdrop-blur-sm font-mono uppercase tracking-widest">
            <Sparkles className="w-3 h-3 mr-2" />
            Inventory Tiers
          </Badge>
          <h2 className="text-4xl md:text-5xl font-default font-bold mb-6 tracking-tighter">
            Timber Cruise Analysis
          </h2>
          <p className="text-lg text-muted-foreground mb-8 font-default">
            Transparent, acreage-based pricing for landowners and foresters. Get defensible volume and valuation data delivered in days, not weeks.
          </p>

          {}
          <div className="flex items-center justify-center">
            <div className="bg-secondary/50 p-1.5 rounded-md flex items-center border border-border/50 backdrop-blur-md">
              <Button variant={!isYearly ? "default" : "ghost"} size="sm" onClick={() => setIsYearly(false)} className={cn("rounded-sm px-6 transition-all font-mono text-xs uppercase tracking-wider", !isYearly && "bg-primary text-primary-foreground shadow-sm")}>
                Standard
              </Button>
              <Button variant={isYearly ? "default" : "ghost"} size="sm" onClick={() => setIsYearly(true)} className={cn("rounded-sm px-6 transition-all font-mono text-xs uppercase tracking-wider", isYearly && "bg-primary text-primary-foreground shadow-sm")}>
                Annual <span className="ml-1.5 text-[10px] opacity-80 font-normal border border-current rounded px-1 py-0.5">-15%</span>
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-8 items-center">
          {pricingTiers.map((tier, i) => <div key={tier.name} data-index={i} className={cn("relative h-full transition-all duration-300", tier.popular ? "md:scale-105 z-10" : "z-0")}>
              <Card className={cn("flex flex-col h-full bg-card/40 backdrop-blur-xl border-border/40", tier.popular ? "border-primary/60 shadow-2xl shadow-black/40 bg-card/80" : "border-border/50 hover:border-primary/30")}>
                {tier.popular && <div className="absolute -top-4 left-0 right-0 flex justify-center">
                    <Badge className="bg-accent-foreground text-background shadow-lg px-3 py-1 text-xs font-mono font-bold uppercase tracking-widest rounded-sm">
                      Recommended
                    </Badge>
                  </div>}

                <CardHeader className={cn("text-center pb-8 pt-10", tier.popular && "pt-12")}>
                  <CardTitle className="text-2xl font-default font-bold text-foreground mb-2 uppercase tracking-tight">
                    {tier.name}
                  </CardTitle>
                  <CardDescription className="text-muted-foreground h-10 flex items-center justify-center font-default text-sm">
                    {tier.description}
                  </CardDescription>
                  <div className="mt-6 flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-mono font-bold tracking-tighter text-foreground">
                      ${isYearly ? tier.priceYearly : tier.priceMonthly}
                    </span>
                    <span className="text-muted-foreground font-mono text-xs uppercase">/base</span>
                  </div>
                  {isYearly && <div className="text-xs text-accent-foreground mt-2 font-mono uppercase tracking-tighter">
                      Billed ${tier.priceYearly * 12} annually
                    </div>}
                  {!isYearly && <div className="text-sm text-transparent mt-2 select-none">
                      Spacer
                    </div>}
                </CardHeader>

                <CardContent className="flex-grow px-8">
                  <ul className="space-y-4">
                    {tier.features.map((feature, index) => <li key={index} data-index={index} className="flex items-start gap-3">
                        <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-sm bg-primary/10 flex items-center justify-center border border-primary/20">
                          <Check className="w-3 h-3 text-primary" />
                        </div>
                        <span className="text-muted-foreground text-sm leading-tight font-default">
                          {feature}
                        </span>
                      </li>)}
                  </ul>
                </CardContent>

                <CardFooter className="pt-8 pb-10 px-8">
                  <Button asChild variant={tier.popular ? "default" : "outline"} className={cn("w-full h-12 text-sm font-mono font-bold uppercase tracking-widest transition-all rounded-sm", tier.popular ? "bg-accent-foreground text-background hover:bg-accent-foreground/90" : "border-primary/40 hover:bg-primary/5")}>
                    <Link to={tier.ctaLink}>{tier.ctaText}</Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}